import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CheckCircle2, ChevronDown, ClipboardPlus, History, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { recordMedicationRegimenEvent, MEDICATION_REGIMEN_EVENT_TYPES } from "@/lib/clinical/medicationRegimenPersistence";
import { displayMedicationName } from "@/lib/pharmacology/smartSubstance";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/lib/safeToast";

function n(v: any) {
  if (v === "" || v == null) return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}
function dateTimeLocal(value = new Date()) {
  const d = new Date(value);
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function eventLabel(type: string) {
  return MEDICATION_REGIMEN_EVENT_TYPES.find(([id]) => id === type)?.[1] ?? type;
}
function fmtDate(value: string) {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

export function MedicationLongitudinalPanel({ medication, open, onOpenChange }: { medication: any; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [eventOpen, setEventOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({
    event_type: "follow_up",
    event_at: dateTimeLocal(),
    previous_dose: medication?.current_dose ?? "",
    new_dose: medication?.current_dose ?? "",
    dose_unit: medication?.dose_unit ?? "mg",
    frequency: medication?.frequency ?? "",
    usual_time: medication?.usual_time?.slice?.(0, 5) ?? medication?.usual_time ?? "",
    benefit_0_100: "",
    adverse_0_100: "",
    adherence_0_100: "",
    reason: "",
    target_update: "",
    clinician_assessment: "",
    patient_report: "",
    monitoring_plan: "",
    next_step: "",
    evidence_context: "observação clínica individual",
  });

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["medication-regimen-events", medication?.id],
    enabled: open && !!medication?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_medication_regimen_events")
        .select("*")
        .eq("patient_medication_id", medication.id)
        .is("deleted_at", null)
        .order("event_at", { ascending: false })
        .limit(120);
      if (error) throw error;
      return data ?? [];
    },
  });

  const latest = events[0];
  const summary = useMemo(() => {
    const withBenefit = events.filter((e: any) => e.benefit_0_100 != null);
    const withAdherence = events.filter((e: any) => e.adherence_0_100 != null);
    const mean = (rows: any[], key: string) => rows.length ? Math.round(rows.reduce((a, e) => a + Number(e[key]), 0) / rows.length) : null;
    return {
      benefit: mean(withBenefit.slice(0, 6), "benefit_0_100"),
      adherence: mean(withAdherence.slice(0, 6), "adherence_0_100"),
      adverse: mean(events.filter((e: any) => e.adverse_0_100 != null).slice(0, 6), "adverse_0_100"),
    };
  }, [events]);

  async function saveEvent() {
    if (!user?.id || !medication?.patient_id) return toast.error("Sessão ou paciente ausente.");
    setSaving(true);
    try {
      await recordMedicationRegimenEvent({
        owner_id: user.id,
        patient_id: medication.patient_id,
        patient_medication_id: medication.id,
        event_at: new Date(form.event_at).toISOString(),
        event_type: form.event_type,
        previous_dose: n(form.previous_dose),
        new_dose: n(form.new_dose),
        dose_unit: form.dose_unit || null,
        frequency: form.frequency || null,
        usual_time: form.usual_time || null,
        status_before: medication.status || null,
        status_after: medication.status || null,
        reason: form.reason || null,
        target_update: form.target_update || null,
        benefit_0_100: n(form.benefit_0_100),
        adverse_0_100: n(form.adverse_0_100),
        adherence_0_100: n(form.adherence_0_100),
        clinician_assessment: form.clinician_assessment || null,
        patient_report: form.patient_report || null,
        monitoring_plan: form.monitoring_plan || null,
        next_step: form.next_step || null,
        evidence_context: form.evidence_context || null,
        source: "acompanhamento longitudinal",
        metadata: {
          medication_name: displayMedicationName(medication),
          canonical_substance: medication.substances?.name ?? null,
          formulation: medication.substance_formulations?.formulation_name ?? null,
        },
      });
      await qc.invalidateQueries({ queryKey: ["medication-regimen-events", medication.id] });
      setEventOpen(false);
      toast.success("Evento longitudinal registrado.");
    } catch (e: any) {
      toast.error(e?.message ?? "Falha ao registrar evento longitudinal.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="inset-0 top-0 flex h-[100dvh] max-h-[100dvh] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[92vh] sm:max-w-5xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
        <DialogHeader className="shrink-0 border-b bg-background/95 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur sm:px-6 sm:pt-5">
          <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4 text-primary" />Acompanhamento longitudinal · {displayMedicationName(medication)}</DialogTitle>
          <div className="text-xs text-muted-foreground">Regime prescrito, mudanças, resposta individual e eventos adversos ficam separados dos registros de ingestão. A dose realmente tomada continua no Journal/Doses.</div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="grid gap-3 md:grid-cols-3">
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Benefício recente</div><div className="mt-1 text-xl font-semibold">{summary.benefit ?? "—"}<span className="text-xs text-muted-foreground">/100</span></div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Adesão recente</div><div className="mt-1 text-xl font-semibold">{summary.adherence ?? "—"}<span className="text-xs text-muted-foreground">/100</span></div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Carga adversa recente</div><div className="mt-1 text-xl font-semibold">{summary.adverse ?? "—"}<span className="text-xs text-muted-foreground">/100</span></div></Card>
          </div>

          <div className="mt-4 rounded-xl border bg-muted/20 p-3 text-xs">
            <div className="font-medium">Estado atual</div>
            <div className="mt-1 grid gap-2 sm:grid-cols-4">
              <div><span className="text-muted-foreground">Dose</span><br />{medication.current_dose ?? "—"} {medication.dose_unit ?? ""}</div>
              <div><span className="text-muted-foreground">Frequência</span><br />{medication.frequency ?? "—"}</div>
              <div><span className="text-muted-foreground">Início</span><br />{medication.start_date ? new Date(medication.start_date).toLocaleDateString("pt-BR") : "—"}</div>
              <div><span className="text-muted-foreground">Status</span><br /><Badge variant="outline">{medication.status ?? "—"}</Badge></div>
            </div>
            {latest?.next_step ? <div className="mt-3 border-t pt-2"><span className="text-muted-foreground">Próximo passo registrado:</span> {latest.next_step}</div> : null}
          </div>

          <div className="mt-5 flex items-center justify-between gap-2">
            <div className="font-medium">Linha do tempo do tratamento</div>
            <Button onClick={() => { setForm((f: any) => ({ ...f, previous_dose: medication.current_dose ?? "", new_dose: medication.current_dose ?? "", frequency: medication.frequency ?? "", usual_time: medication.usual_time?.slice?.(0,5) ?? medication.usual_time ?? "", event_at: dateTimeLocal() })); setEventOpen(true); }}><ClipboardPlus className="mr-1.5 h-4 w-4" />Registrar acompanhamento</Button>
          </div>

          {isLoading ? <div className="py-6 text-sm text-muted-foreground">Carregando histórico…</div> : events.length === 0 ? (
            <Card className="mt-3 p-5 text-sm text-muted-foreground">Nenhum evento longitudinal ainda. Registre o início, ajuste de dose ou uma consulta de seguimento.</Card>
          ) : (
            <div className="relative mt-4 space-y-3 pl-4 before:absolute before:bottom-0 before:left-[6px] before:top-1 before:w-px before:bg-border">
              {events.map((event: any) => (
                <div key={event.id} className="relative rounded-xl border bg-card p-3">
                  <span className="absolute -left-[1.28rem] top-4 h-3 w-3 rounded-full border-2 border-background bg-primary" />
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{eventLabel(event.event_type)}</Badge>
                    <span className="text-xs text-muted-foreground"><CalendarClock className="mr-1 inline h-3 w-3" />{fmtDate(event.event_at)}</span>
                  </div>
                  <div className="mt-2 grid gap-2 text-xs sm:grid-cols-4">
                    <div><span className="text-muted-foreground">Dose</span><br />{event.new_dose ?? "—"} {event.dose_unit ?? medication.dose_unit ?? ""}</div>
                    <div><span className="text-muted-foreground">Benefício</span><br />{event.benefit_0_100 ?? "—"}/100</div>
                    <div><span className="text-muted-foreground">Adverso</span><br />{event.adverse_0_100 ?? "—"}/100</div>
                    <div><span className="text-muted-foreground">Adesão</span><br />{event.adherence_0_100 ?? "—"}/100</div>
                  </div>
                  {event.reason ? <div className="mt-2"><b>Motivo:</b> {event.reason}</div> : null}
                  {event.clinician_assessment ? <div className="mt-1"><b>Avaliação:</b> {event.clinician_assessment}</div> : null}
                  {event.patient_report ? <div className="mt-1 text-muted-foreground"><b>Relato:</b> {event.patient_report}</div> : null}
                  {event.target_update ? <div className="mt-1"><b>Alvo:</b> {event.target_update}</div> : null}
                  {event.monitoring_plan ? <div className="mt-1"><b>Monitorização:</b> {event.monitoring_plan}</div> : null}
                  {event.next_step ? <div className="mt-1"><b>Próximo passo:</b> {event.next_step}</div> : null}
                  {event.evidence_context ? <div className="mt-2 text-[11px] text-muted-foreground">Base interpretativa: {event.evidence_context}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <Dialog open={eventOpen} onOpenChange={setEventOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle>Novo evento longitudinal</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tipo"><select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}><option value="follow_up">Seguimento clínico</option>{MEDICATION_REGIMEN_EVENT_TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></Field>
            <Field label="Data/hora"><Input type="datetime-local" value={form.event_at} onChange={(e) => setForm({ ...form, event_at: e.target.value })} /></Field>
            <Field label="Dose anterior"><Input type="number" step="any" value={form.previous_dose} onChange={(e) => setForm({ ...form, previous_dose: e.target.value })} /></Field>
            <Field label="Nova dose"><Input type="number" step="any" value={form.new_dose} onChange={(e) => setForm({ ...form, new_dose: e.target.value })} /></Field>
            <Field label="Unidade"><Input value={form.dose_unit} onChange={(e) => setForm({ ...form, dose_unit: e.target.value })} /></Field>
            <Field label="Frequência"><Input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} /></Field>
            <Field label="Horário usual"><Input type="time" value={form.usual_time} onChange={(e) => setForm({ ...form, usual_time: e.target.value })} /></Field>
            <Field label="Benefício · 0–100"><Input type="number" min={0} max={100} value={form.benefit_0_100} onChange={(e) => setForm({ ...form, benefit_0_100: e.target.value })} /></Field>
            <Field label="Adverso · 0–100"><Input type="number" min={0} max={100} value={form.adverse_0_100} onChange={(e) => setForm({ ...form, adverse_0_100: e.target.value })} /></Field>
            <Field label="Adesão · 0–100"><Input type="number" min={0} max={100} value={form.adherence_0_100} onChange={(e) => setForm({ ...form, adherence_0_100: e.target.value })} /></Field>
            <Field label="Motivo / contexto"><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field>
            <Field label="Atualização do alvo"><Textarea rows={2} value={form.target_update} onChange={(e) => setForm({ ...form, target_update: e.target.value })} /></Field>
            <Field label="Avaliação clínica"><Textarea rows={3} value={form.clinician_assessment} onChange={(e) => setForm({ ...form, clinician_assessment: e.target.value })} /></Field>
            <Field label="Relato do paciente"><Textarea rows={3} value={form.patient_report} onChange={(e) => setForm({ ...form, patient_report: e.target.value })} /></Field>
            <Field label="Plano de monitorização"><Textarea rows={3} value={form.monitoring_plan} onChange={(e) => setForm({ ...form, monitoring_plan: e.target.value })} /></Field>
            <Field label="Próximo passo"><Textarea rows={3} value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} /></Field>
            <Field label="Base interpretativa"><Input value={form.evidence_context} onChange={(e) => setForm({ ...form, evidence_context: e.target.value })} placeholder="bula, guideline, estudo, observação individual…" /></Field>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEventOpen(false)}>Cancelar</Button><Button onClick={saveEvent} disabled={saving}>{saving ? "Salvando…" : "Salvar evento"}</Button></div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function Field({ label, children }: any) {
  return <div className="min-w-0 space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}
